import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const SERVICES = [
  {
    icon: "🏠",
    title: "Residential Construction",
    desc: "Complete home building from foundation to roof — 1BHK to 4BHK and beyond, tailored to your vision and lifestyle.",
    features: [
      "RCC Frame Structure",
      "Premium Tile Flooring",
      "Vastu-Compliant Layout",
      "Electrical & Plumbing",
      "Door & Window Fitting",
    ],
  },
  {
    icon: "🔧",
    title: "Renovation & Remodeling",
    desc: "Transform your existing space. Full kitchen, bathroom, bedroom, and whole-home renovations with minimal disruption.",
    features: [
      "Structural Modifications",
      "New Flooring & Tiles",
      "Modern Kitchen Setup",
      "Bathroom Remodel",
      "Interior Upgrades",
    ],
  },
  {
    icon: "🎨",
    title: "Painting & Finishing",
    desc: "Premium interior and exterior painting, texture work, and polished finishes using branded paints that last years.",
    features: [
      "Asian Paints / Berger",
      "Texture & Stucco Work",
      "Waterproofing Coat",
      "Wood Polish & Lacquer",
      "Anti-Fungal Coating",
    ],
  },
  {
    icon: "🧱",
    title: "Lenting & Plastering",
    desc: "Expert lenting and plastering for smooth, durable walls. Perfect wall finishing every time with quality cement mix.",
    features: [
      "12mm Plaster Coat",
      "POP Ceiling Work",
      "Wall Putty Finish",
      "Corner Beading",
      "Crack-Proof Formula",
    ],
  },
  {
    icon: "💧",
    title: "Borwell Drilling",
    desc: "Professional borwell drilling services for reliable groundwater access. Full site assessment and casing installation included.",
    features: [
      "Site Assessment",
      "Rotary Drilling",
      "PVC Casing Pipe",
      "Submersible Pump",
      "Water Testing",
    ],
  },
  {
    icon: "🏢",
    title: "Commercial Construction",
    desc: "Shops, offices, warehouses and commercial complexes — we handle all scales of commercial construction with precision.",
    features: [
      "Shop/Office Layout",
      "Commercial Flooring",
      "False Ceiling",
      "Electrical Panels",
      "Fire Safety Compliance",
    ],
  },
  {
    icon: "🏛️",
    title: "Government Contracts",
    desc: "Certified and experienced in government infrastructure — school buildings, roads, public facilities. Fast, compliant delivery.",
    features: [
      "Govt. Tender Certified",
      "Public Works Standards",
      "Timely Completion",
      "Documentation Ready",
      "Quality Audits",
    ],
  },
  {
    icon: "🪟",
    title: "Structural & Civil Work",
    desc: "RCC columns, beams, slabs, boundary walls and all civil structural work using ISI-marked materials and tested mixes.",
    features: [
      "RCC Design",
      "Boundary Walls",
      "Retaining Walls",
      "Staircase Work",
      "Beam & Column",
    ],
  },
];

const RATES = [
  { label: "Residential (1 floor)", rate: 1200 },
  { label: "Residential (2 floor)", rate: 1450 },
  { label: "Premium Home", rate: 1700 },
  { label: "Commercial", rate: 1350 },
  { label: "Renovation", rate: 850 },
  { label: "Boundary Wall (rft)", rate: 400 },
  { label: "Painting (sq ft)", rate: 25 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function Services() {
  const [rate, setRate] = useState(1200);
  const [area, setArea] = useState(1000);
  const estimate = rate * area;

  return (
    <>
      <Helmet>
        <title>Services — NirmalRam Construction</title>
        <meta
          name="description"
          content="NirmalRam Construction offers residential construction, renovation, painting, borwell, lenting, and government contract services in Nandurbar, Maharashtra."
        />
      </Helmet>

      {/* Header */}
      <section
        style={{
          background: "#0A0A0A",
          padding: "8rem 0 4rem",
          borderBottom: "1px solid rgba(201,168,76,0.12)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="grid-bg" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="section-tag">Our Services</span>
            <h1
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "1rem",
              }}
            >
              Everything You Need
              <br />
              to Build Your Dream
            </h1>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "520px", lineHeight: 1.8 }}>
              From foundation to final finish — we handle every aspect of
              construction with skilled teams and quality materials at
              competitive market rates.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
              gap: "1.75rem",
            }}
          >
            {SERVICES.map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                whileHover={{ y: -6, borderColor: "rgba(201,168,76,0.4)" }}
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.12)",
                  borderRadius: "3px",
                  padding: "2rem",
                  transition: "all 0.3s",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background: `linear-gradient(90deg, transparent, #C9A84C ${i * 15 + 20}%, transparent)`,
                  }}
                />
                <div
                  style={{
                    width: "54px",
                    height: "54px",
                    background: "rgba(201,168,76,0.07)",
                    border: "1px solid rgba(201,168,76,0.18)",
                    borderRadius: "3px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.6rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  {s.icon}
                </div>
                <h3 style={{ fontSize: "1.2rem", marginBottom: "0.75rem" }}>
                  {s.title}
                </h3>
                <p
                  style={{
                    color: "#8A8070",
                    fontSize: "0.88rem",
                    lineHeight: 1.7,
                    marginBottom: "1.25rem",
                  }}
                >
                  {s.desc}
                </p>
                <div
                  style={{
                    borderTop: "1px solid rgba(201,168,76,0.1)",
                    paddingTop: "1rem",
                  }}
                >
                  {s.features.map((f) => (
                    <div
                      key={f}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginBottom: "0.4rem",
                      }}
                    >
                      <span style={{ color: "#C9A84C", fontSize: "0.75rem" }}>
                        ◆
                      </span>
                      <span style={{ color: "#5A5450", fontSize: "0.82rem" }}>
                        {f}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Cost Estimator */}
      <section
        className="section"
        style={{
          background: "#0A0A0A",
          borderTop: "1px solid rgba(201,168,76,0.1)",
        }}
      >
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4rem",
              alignItems: "center",
            }}
          >
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <span className="section-tag">Quick Estimate</span>
              <h2>Construction Cost Estimator</h2>
              <div className="section-divider" />
              <p
                style={{
                  color: "#8A8070",
                  lineHeight: 1.8,
                  marginBottom: "1.5rem",
                }}
              >
                Get a rough idea of your project cost based on current Nandurbar
                market rates. Final cost depends on materials, site, and
                finishes chosen.
              </p>
              <p
                style={{
                  color: "#5A5450",
                  fontSize: "0.85rem",
                  fontStyle: "italic",
                }}
              >
                * Material costs vary as per market rates. We provide
                transparent billing.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div
                style={{
                  background: "#111",
                  border: "1px solid rgba(201,168,76,0.15)",
                  padding: "2.5rem",
                  borderRadius: "3px",
                }}
              >
                <div className="form-group">
                  <label>Construction Type</label>
                  <select
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value))}
                  >
                    {RATES.map((r) => (
                      <option key={r.label} value={r.rate}>
                        {r.label} — ₹{r.rate}/sq ft
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Area (sq ft)</label>
                  <input
                    type="number"
                    value={area}
                    onChange={(e) => setArea(Number(e.target.value))}
                    min="100"
                    placeholder="Enter area in sq ft"
                  />
                </div>
                <div
                  style={{
                    background: "rgba(201,168,76,0.06)",
                    border: "1px solid rgba(201,168,76,0.2)",
                    borderRadius: "3px",
                    padding: "2rem",
                    textAlign: "center",
                    marginBottom: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.72rem",
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      color: "#5A5450",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Estimated Cost
                  </div>
                  <div
                    style={{
                      fontFamily: "Cormorant Garamond,serif",
                      fontSize: "3rem",
                      color: "#C9A84C",
                    }}
                  >
                    ₹{estimate.toLocaleString("en-IN")}
                  </div>
                  <div
                    style={{
                      fontSize: "0.78rem",
                      color: "#5A5450",
                      marginTop: "0.5rem",
                    }}
                  >
                    Approximate. Actual cost may vary.
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="btn-gold"
                  style={{
                    display: "block",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  Get Detailed Quote →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
        <style>{`@media(max-width:768px){.container > div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important;}}`}</style>
      </section>
    </>
  );
}
